import axios from 'axios';
import { getApiBaseUrl } from '@/config/environment';
import {
  SubscriptionsListResponse,
  PremiumFleetPricing,
  SubscriptionFilters,
  ActivateSubscriptionRequest,
  ActivateSubscriptionResponse,
  UpdatePricingRequest,
  UpdatePricingResponse,
  SubscriptionHistory,
  HostSubscriptionInfo,
  FleetCriteria,
  UpdateCriteriaRequest,
  UpdateCriteriaResponse,
} from '../pages/fleet-subscriptions/types/subscription.types';


// Helper to get auth token
const getAuthToken = (): string => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found');
  }
  return token;
};

// Helper to create axios config with auth
const getAuthConfig = () => ({
  headers: {
    Authorization: `Bearer ${getAuthToken()}`,
    'Content-Type': 'application/json',
  },
});

/**
 * Transform backend response to frontend format
 */
const transformHostData = (backendHost: any): HostSubscriptionInfo => {
  return {
    user_id: parseInt(backendHost.id) || 0,
    user_uuid: backendHost.id,
    full_name: backendHost.name || 'Unknown',
    email: backendHost.email || '',
    phone_number: backendHost.phone || '',
    profile_picture: null,
    host_type: backendHost.hostType,
    vehicle_count: backendHost.vehicleCount || 0,
    active_subscription: backendHost.subscription?.isActive
      ? {
          id: 0,
          user_id: 0,
          subscription_type: 'premium_fleet',
          status: 'active',
          start_date: backendHost.subscription.startedAt,
          end_date: backendHost.subscription.expiresAt,
          auto_renew: false,
          pricing_id: 0,
          payment_reference: backendHost.subscription.paymentId,
          activated_by: 0,
          notes: null,
          created_at: backendHost.createdAt,
          updated_at: backendHost.createdAt,
        }
      : null,
    qualification_path: backendHost.qualificationPath,
    created_at: backendHost.createdAt,
  };
};

/**
 * Fetch all hosts with their subscription status
 */
export const fetchFleetSubscriptions = async (
  filters: SubscriptionFilters = {}
): Promise<SubscriptionsListResponse> => {
  try {
    const params = new URLSearchParams();

    if (filters.search) params.append('search', filters.search);
    if (filters.host_type && filters.host_type !== 'all') {
      params.append('hostType', filters.host_type);
    }
    if (filters.subscription_status && filters.subscription_status !== 'all') {
      params.append('subscriptionStatus', filters.subscription_status);
    }
    if (filters.qualification && filters.qualification !== 'all') {
      params.append('qualification', filters.qualification);
    }
    if (filters.expiring_soon) params.append('expiringSoon', 'true');
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sort_by) params.append('sortBy', filters.sort_by);
    if (filters.sort_order) params.append('sortOrder', filters.sort_order);

    const response = await axios.get<any>(
      `${getApiBaseUrl()}/api/v1/admin/fleet/subscriptions?${params.toString()}`,
      getAuthConfig()
    );

    const backendData = response.data.data;

    // Transform backend response to frontend format
    return {
      hosts: backendData.hosts.map(transformHostData),
      summary: {
        total_hosts: backendData.summary.totalHosts || 0,
        fleet_managers: backendData.summary.fleetManagers || 0,
        individuals: backendData.summary.individuals || 0,
        active_subscriptions: backendData.summary.activeSubscriptions || 0,
        expired_subscriptions: backendData.summary.expiredSubscriptions || 0,
        qualified_by_vehicles: backendData.summary.qualifiedByVehicles || 0,
        qualified_by_premium: backendData.summary.qualifiedByPremium || 0,
        qualified_by_both: backendData.summary.qualifiedByBoth || 0,
        not_qualified: backendData.summary.notQualified || 0,
        total_revenue: 0,
      },
      pagination: {
        total: backendData.hosts.length,
        page: filters.page || 1,
        limit: filters.limit || 25,
        total_pages: Math.ceil(backendData.hosts.length / (filters.limit || 25)),
      },
    };
  } catch (error: any) {
    console.error('Error fetching fleet subscriptions:', error);
    throw new Error(
      error.response?.data?.error || 'Failed to fetch fleet subscriptions'
    );
  }
};

/**
 * Fetch current active pricing configuration
 */
export const fetchCurrentPricing = async (): Promise<PremiumFleetPricing> => {
  try {
    const response = await axios.get<any>(
      `${getApiBaseUrl()}/api/v1/admin/fleet/subscriptions/pricing`,
      getAuthConfig()
    );

    const backendPricing = response.data.data.pricing;

    // Transform backend response to frontend format
    return {
      id: backendPricing.id,
      monthly_rate: backendPricing.monthlyPrice,
      quarterly_rate: backendPricing.quarterlyPrice,
      yearly_rate: backendPricing.yearlyPrice,
      effective_from: backendPricing.effectiveFrom,
      effective_to: backendPricing.effectiveUntil,
      is_active: true,
      created_by: 1,
      notes: backendPricing.notes,
      created_at: backendPricing.createdAt,
      updated_at: backendPricing.updatedAt,
    };
  } catch (error: any) {
    console.error('Error fetching pricing:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch pricing');
  }
};

/**
 * Fetch pricing history
 */
export const fetchPricingHistory = async (): Promise<PremiumFleetPricing[]> => {
  try {
    const response = await axios.get<{ pricing_history: PremiumFleetPricing[] }>(
      `${getApiBaseUrl()}/api/v1/admin/fleet/subscriptions/pricing/history`,
      getAuthConfig()
    );

    return response.data.pricing_history;
  } catch (error: any) {
    console.error('Error fetching pricing history:', error);
    throw new Error(
      error.response?.data?.error || 'Failed to fetch pricing history'
    );
  }
};

/**
 * Update subscription pricing
 */
export const updatePricing = async (
  pricingData: UpdatePricingRequest
): Promise<UpdatePricingResponse> => {
  try {
    const response = await axios.patch<UpdatePricingResponse>(
      `${getApiBaseUrl()}/api/v1/admin/fleet/subscriptions/pricing`,
      pricingData,
      getAuthConfig()
    );

    return response.data;
  } catch (error: any) {
    console.error('Error updating pricing:', error);
    throw new Error(error.response?.data?.error || 'Failed to update pricing');
  }
};

/**
 * Activate premium subscription for a user
 */
export const activateSubscription = async (
  userId: number,
  subscriptionData: ActivateSubscriptionRequest
): Promise<ActivateSubscriptionResponse> => {
  try {
    const response = await axios.post<ActivateSubscriptionResponse>(
      `${getApiBaseUrl()}/api/v1/admin/fleet/subscriptions/${userId}/activate`,
      subscriptionData,
      getAuthConfig()
    );

    return response.data;
  } catch (error: any) {
    console.error('Error activating subscription:', error);
    throw new Error(
      error.response?.data?.error || 'Failed to activate subscription'
    );
  }
};

/**
 * Fetch subscription history for a specific user
 */
export const fetchUserSubscriptionHistory = async (
  userId: number
): Promise<SubscriptionHistory[]> => {
  try {
    const response = await axios.get<{ history: SubscriptionHistory[] }>(
      `${getApiBaseUrl()}/api/v1/admin/fleet/subscriptions/${userId}/history`,
      getAuthConfig()
    );

    return response.data.history;
  } catch (error: any) {
    console.error('Error fetching subscription history:', error);
    throw new Error(
      error.response?.data?.error || 'Failed to fetch subscription history'
    );
  }
};

/**
 * Export subscriptions to CSV
 */
export const exportSubscriptionsCSV = async (
  filters: SubscriptionFilters = {}
): Promise<Blob> => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== 'all') {
        params.append(key, value.toString());
      }
    });

    const response = await axios.get(
      `${getApiBaseUrl()}/api/v1/admin/fleet/subscriptions/export?${params.toString()}`,
      {
        ...getAuthConfig(),
        responseType: 'blob',
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Error exporting subscriptions:', error);
    throw new Error(
      error.response?.data?.error || 'Failed to export subscriptions'
    );
  }
};

/**
 * Calculate discount percentages from pricing
 */
export const calculateDiscounts = (pricing: {
  monthly_rate: number;
  quarterly_rate: number;
  yearly_rate: number;
}) => {
  const monthlyTotal = pricing.monthly_rate * 3;
  const quarterlyDiscount =
    monthlyTotal > 0
      ? ((monthlyTotal - pricing.quarterly_rate) / monthlyTotal) * 100
      : 0;

  const yearlyMonthlyTotal = pricing.monthly_rate * 12;
  const yearlyDiscount =
    yearlyMonthlyTotal > 0
      ? ((yearlyMonthlyTotal - pricing.yearly_rate) / yearlyMonthlyTotal) * 100
      : 0;

  return {
    quarterly_discount_percent: Math.round(quarterlyDiscount * 10) / 10,
    yearly_discount_percent: Math.round(yearlyDiscount * 10) / 10,
  };
};

/**
 * Calculate discounted rates based on monthly rate and discount percentage
 */
export const calculateRatesFromDiscount = (
  monthlyRate: number,
  discountPercent: number
) => {
  const quarterlyBase = monthlyRate * 3;
  const yearlyBase = monthlyRate * 12;

  return {
    monthly_rate: monthlyRate,
    quarterly_rate: Math.round(quarterlyBase * (1 - discountPercent / 100)),
    yearly_rate: Math.round(yearlyBase * (1 - discountPercent / 100)),
  };
};

/**
 * Format currency in Naira
 */
export const formatNaira = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Get subscription duration label
 */
export const getDurationLabel = (months: number): string => {
  if (months === 1) return '1 Month';
  if (months === 3) return '3 Months';
  if (months === 12) return '12 Months';
  return `${months} Months`;
};

/**
 * Calculate days until expiry
 */
export const getDaysUntilExpiry = (endDate: string): number => {
  const end = new Date(endDate);
  const now = new Date();
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Check if subscription is expiring soon (within 30 days)
 */
export const isExpiringSoon = (endDate: string): boolean => {
  const daysLeft = getDaysUntilExpiry(endDate);
  return daysLeft > 0 && daysLeft <= 30;
};

/**
 * Fetch fleet criteria configuration
 */
export const fetchFleetCriteria = async (): Promise<FleetCriteria> => {
  try {
    const response = await axios.get<any>(
      `${getApiBaseUrl()}/api/v1/admin/fleet/criteria`,
      getAuthConfig()
    );

    const backendCriteria = response.data.data;

    // Transform backend response to frontend format
    return {
      min_vehicles: backendCriteria.minVehicles,
      verification_period_days: backendCriteria.verificationPeriodDays,
      grace_period_days: backendCriteria.gracePeriodDays,
      auto_downgrade_enabled: backendCriteria.autoDowngradeEnabled,
      last_updated: backendCriteria.lastUpdated,
      updated_by: backendCriteria.updatedBy,
      update_reason: backendCriteria.updateReason,
    };
  } catch (error: any) {
    console.error('Error fetching fleet criteria:', error);
    throw new Error(
      error.response?.data?.error || 'Failed to fetch fleet criteria'
    );
  }
};

/**
 * Update fleet criteria configuration
 */
export const updateFleetCriteria = async (
  criteriaData: UpdateCriteriaRequest
): Promise<UpdateCriteriaResponse> => {
  try {
    // Transform frontend request to backend format
    const backendRequest: any = {
      updateReason: criteriaData.update_reason,
    };

    if (criteriaData.min_vehicles !== undefined) {
      backendRequest.minVehicles = criteriaData.min_vehicles;
    }
    if (criteriaData.verification_period_days !== undefined) {
      backendRequest.verificationPeriodDays = criteriaData.verification_period_days;
    }
    if (criteriaData.grace_period_days !== undefined) {
      backendRequest.gracePeriodDays = criteriaData.grace_period_days;
    }
    if (criteriaData.auto_downgrade_enabled !== undefined) {
      backendRequest.autoDowngradeEnabled = criteriaData.auto_downgrade_enabled;
    }
    if (criteriaData.apply_to_existing !== undefined) {
      backendRequest.applyToExisting = criteriaData.apply_to_existing;
    }

    const response = await axios.patch<any>(
      `${getApiBaseUrl()}/api/v1/admin/fleet/criteria`,
      backendRequest,
      getAuthConfig()
    );

    const backendCriteria = response.data.data;

    return {
      success: response.data.success,
      message: response.data.message || 'Criteria updated successfully',
      criteria: {
        min_vehicles: backendCriteria.minVehicles,
        verification_period_days: backendCriteria.verificationPeriodDays,
        grace_period_days: backendCriteria.gracePeriodDays,
        auto_downgrade_enabled: backendCriteria.autoDowngradeEnabled,
        last_updated: backendCriteria.lastUpdated,
        updated_by: backendCriteria.updatedBy,
        update_reason: backendCriteria.updateReason,
      },
    };
  } catch (error: any) {
    console.error('Error updating fleet criteria:', error);
    throw new Error(
      error.response?.data?.error || 'Failed to update fleet criteria'
    );
  }
};
